import { useEffect, useState } from 'react'

import { ElpPromotionFilter } from '~/app/common/enums/elp-promotion-filter'

type useElpPromotionsProps = {
  elpOffers: Record<string, any>[]
  promotionsContent: Record<string, any>[]
  filterOption?: ElpPromotionFilter
  searchQuery?: string
  offersLoading: boolean
}

const useElpPromotions = ({
  elpOffers,
  promotionsContent,
  filterOption,
  searchQuery,
  offersLoading,
}: useElpPromotionsProps) => {
  const [promotions, setPromotions] = useState<Record<string, any>>([])
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)

    return () => clearTimeout(handler)
  }, [searchQuery])

  useEffect(() => {
    if (elpOffers?.length && promotionsContent?.length && !offersLoading) {
      const offersMap: Record<string, any> = {}

      elpOffers?.forEach((offer) => {
        offersMap[offer?.instanceId] = offer
      })

      const availableOfferKeys = Object.keys(offersMap)

      const updatedPromotions = promotionsContent
        ?.filter((promotion) =>
          promotion?.instanceIds
            ?.split(',')
            .some((id: string) => availableOfferKeys?.includes(id)),
        )
        ?.map((promotion) => {
          const offer = elpOffers?.find((offer) =>
            promotion?.instanceIds?.split(',')?.includes(offer?.instanceId),
          )

          // NOTE: adding this here so that instance ids don't contain a config mistake
          // where we have an instance id which is not supported by ELP.
          promotion.instanceIds = promotion?.instanceIds
            ?.split(',')
            ?.filter((id: string) => availableOfferKeys?.includes(id))
            ?.join(',')

          promotion.memberOptedIn = promotion?.instanceIds
            ?.split(',')
            ?.every((id: string) => {
              const offer = offersMap[id]
              return offer?.memberOptedIn === true
            })

          return {
            ...offer,
            ...promotion,
          }
        })
        ?.filter((promotion) => {
          if (filterOption === ElpPromotionFilter.Available)
            return promotion?.memberOptedIn === false

          if (filterOption === ElpPromotionFilter.Enrolled)
            return promotion?.memberOptedIn === true

          return true
        })
        ?.filter((promotion) => {
          if (!debouncedSearchQuery) return true

          const query = debouncedSearchQuery.toLowerCase()

          return (
            promotion?.title?.toLowerCase()?.includes(query) ||
            promotion?.name?.toLowerCase()?.includes(query) ||
            promotion?.brand?.toLowerCase()?.includes(query) ||
            promotion?.type?.toLowerCase()?.includes(query) ||
            promotion?.promotionDescription?.toLowerCase()?.includes(query)
          )
        })

      setPromotions(updatedPromotions)
    } else {
      setPromotions([])
    }
  }, [
    elpOffers?.length,
    promotionsContent?.length,
    filterOption,
    debouncedSearchQuery,
    offersLoading,
  ])

  return { promotions }
}

export { useElpPromotions }
